import type { CSSProperties } from 'react';
import type { DeckElement, DeckSlide, DeckTheme } from '@/lib/slideDeck';

const SHAPE_CLIP: Record<string, string | undefined> = {
  triangle: 'polygon(50% 0%, 100% 100%, 0% 100%)',
  diamond: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
  star: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
  arrow: 'polygon(0% 35%, 62% 35%, 62% 0%, 100% 50%, 62% 100%, 62% 65%, 0% 65%)',
};

function chartSvg(el: DeckElement) {
  const chart = el.chart;
  if (!chart || !chart.values.length) return null;
  const values = chart.values;
  const max = Math.max(...values, 1);
  if (chart.kind === 'pie') {
    const total = values.reduce((a, b) => a + b, 0) || 1;
    let acc = 0;
    const palette = ['#0f766e', '#c2410c', '#a16207', '#4d7c0f', '#9d174d', '#1e40af', '#0e7490'];
    return (
      <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" style={{ width: '100%', height: '100%' }}>
        {values.map((v, i) => {
          const start = (acc / total) * Math.PI * 2;
          acc += v;
          const end = (acc / total) * Math.PI * 2;
          const r = 46;
          const cx = 50;
          const cy = 50;
          const x1 = cx + r * Math.sin(start);
          const y1 = cy - r * Math.cos(start);
          const x2 = cx + r * Math.sin(end);
          const y2 = cy - r * Math.cos(end);
          const large = end - start > Math.PI ? 1 : 0;
          const d = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
          return <path key={i} d={d} fill={palette[i % palette.length]} opacity={0.92} />;
        })}
      </svg>
    );
  }
  if (chart.kind === 'line') {
    const step = values.length > 1 ? 90 / (values.length - 1) : 0;
    const points = values.map((v, i) => `${5 + i * step},${95 - (v / max) * 80}`).join(' ');
    return (
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
        <polyline points={points} fill="none" stroke={el.fill || '#0f766e'} strokeWidth={2} />
        {values.map((v, i) => {
          const cx = 5 + i * step;
          const cy = 95 - (v / max) * 80;
          return <circle key={i} cx={cx} cy={cy} r={1.6} fill={el.fill || '#0f766e'} />;
        })}
      </svg>
    );
  }
  const barW = 100 / (values.length * 1.6);
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
      {values.map((v, i) => {
        const h = Math.max(2, (v / max) * 82);
        return (
          <rect
            key={i}
            x={6 + i * (barW * 1.6)}
            y={94 - h}
            width={barW}
            height={h}
            rx={1.4}
            fill={el.fill || '#0f766e'}
            opacity={0.9}
          />
        );
      })}
    </svg>
  );
}

export function elementInnerStyle(el: DeckElement): CSSProperties {
  const style: CSSProperties = {
    position: 'absolute',
    left: `${el.x}%`,
    top: `${el.y}%`,
    width: `${el.w}%`,
    height: `${el.h}%`,
    transform: el.rotation ? `rotate(${el.rotation}deg)` : undefined,
    opacity: el.opacity === undefined ? 1 : el.opacity,
    boxSizing: 'border-box',
  };
  return style;
}

interface Props {
  slide: DeckSlide;
  theme: DeckTheme;
  /** Affiche le lien cliquable (mode lecture seule). */
  interactive?: boolean;
}

/** Rendu statique d'une diapositive (aucune interaction d'édition). */
export default function DeckSlideStatic({ slide, theme, interactive = false }: Props) {
  return (
    <div
      className="relative w-full overflow-hidden"
      style={{
        aspectRatio: '16 / 9',
        backgroundColor: slide.bg || theme.background,
        backgroundImage: slide.bgImage ? `url(${slide.bgImage})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        containerType: 'inline-size',
        fontFamily: theme.bodyFont,
      }}
    >
      {slide.elements.map((el) => {
        const common = elementInnerStyle(el);
        if (el.type === 'image') {
          const img = (
            <img
              src={el.src}
              alt=""
              draggable={false}
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', borderRadius: el.radius ? `${el.radius}%` : undefined }}
            />
          );
          return (
            <div key={el.id} style={common}>
              {interactive && el.link ? (
                <a href={el.link} target="_blank" rel="nofollow" style={{ display: 'block', width: '100%', height: '100%' }}>
                  {img}
                </a>
              ) : (
                img
              )}
            </div>
          );
        }
        if (el.type === 'shape') {
          return (
            <div
              key={el.id}
              style={{
                ...common,
                background: el.fill || theme.primary,
                borderRadius: el.shape === 'ellipse' ? '50%' : el.shape === 'roundRect' ? `${el.radius ?? 12}px` : undefined,
                clipPath: el.shape ? SHAPE_CLIP[el.shape] : undefined,
                border: el.stroke ? `${el.strokeWidth || 1}px solid ${el.stroke}` : undefined,
                boxShadow: el.shadow ? '0 10px 30px rgba(15,23,42,0.18)' : undefined,
              }}
            />
          );
        }
        if (el.type === 'line') {
          return (
            <div key={el.id} style={{ ...common, display: 'flex', alignItems: 'center' }}>
              <div style={{ width: '100%', height: `${el.strokeWidth || 2}px`, background: el.stroke || el.fill || theme.text }} />
            </div>
          );
        }
        if (el.type === 'chart') {
          return (
            <div key={el.id} style={common}>
              {el.chart?.title && (
                <p
                  style={{
                    fontSize: '2.2cqw',
                    margin: '0 0 1.4cqw',
                    fontWeight: 600,
                    color: el.color || theme.text,
                    fontFamily: theme.headingFont,
                  }}
                >
                  {el.chart.title}
                </p>
              )}
              <div style={{ width: '100%', height: el.chart?.title ? 'calc(100% - 4cqw)' : '100%' }}>{chartSvg(el)}</div>
            </div>
          );
        }
        if (el.type === 'table' && el.table) {
          return (
            <div key={el.id} style={{ ...common, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '2cqw', color: el.color || theme.text }}>
                <thead>
                  <tr>
                    {el.table.headers.map((h, i) => (
                      <th
                        key={i}
                        style={{
                          border: '1px solid rgba(120,120,120,0.35)',
                          padding: '0.8cqw 1.2cqw',
                          background: el.fill || 'rgba(15,118,110,0.12)',
                          textAlign: 'left',
                          fontWeight: 600,
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {el.table.rows.map((row, ri) => (
                    <tr key={ri}>
                      {row.map((cell, ci) => (
                        <td key={ci} style={{ border: '1px solid rgba(120,120,120,0.28)', padding: '0.7cqw 1.2cqw' }}>
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        if (el.type === 'video' && el.videoUrl) {
          const url = el.videoUrl;
          const youtube = /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]+)/.exec(url);
          const src = youtube ? `https://www.youtube.com/embed/${youtube[1]}` : url;
          return (
            <div key={el.id} style={{ ...common, overflow: 'hidden', borderRadius: '6px' }}>
              <iframe
                title="Vidéo de la diapositive"
                src={src}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ width: '100%', height: '100%', border: 0 }}
              />
            </div>
          );
        }
        const isWordArt = el.type === 'wordart';
        const justifyContent = el.valign === 'middle' ? 'center' : el.valign === 'bottom' ? 'flex-end' : 'flex-start';
        return (
          <div
            key={el.id}
            style={{
              ...common,
              display: 'flex',
              flexDirection: 'column',
              justifyContent,
              textAlign: el.align || 'left',
              padding: '0.6cqw 0.8cqw',
              background: el.fill || 'transparent',
              borderRadius: el.radius ? '1cqw' : undefined,
              overflow: 'hidden',
              boxShadow: el.shadow ? '0 8px 24px rgba(15,23,42,0.18)' : undefined,
            }}
          >
            <div
              style={{
                fontSize: `${el.fontSize ?? 3}cqw`,
                fontFamily: isWordArt ? theme.headingFont : theme.bodyFont,
                color: el.color || theme.text,
                fontWeight: el.bold || isWordArt ? 700 : 400,
                fontStyle: el.italic ? 'italic' : undefined,
                textDecoration: el.underline ? 'underline' : undefined,
                lineHeight: 1.25,
                wordBreak: 'break-word',
                WebkitTextStroke: isWordArt ? '0.15cqw rgba(255,255,255,0.35)' : undefined,
                textShadow: isWordArt ? '0 0.3cqw 0.6cqw rgba(15,23,42,0.25)' : undefined,
              }}
              dangerouslySetInnerHTML={{ __html: el.html || '' }}
            />
          </div>
        );
      })}
    </div>
  );
}